import clsx from "clsx";
import {
  ArrowDownToDot,
  EllipsisVertical,
  LucideIcon,
  Merge,
  Pencil,
  RotateCw,
  Sparkles,
  SquareSplitVertical,
  Trash2,
} from "lucide-react";
import { FC, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { DoclistTreeItem, DoclistTreeItemSectionsItem } from "~/api/model";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { trackEvent } from "~/utils/analytics";
import { CategoryItem } from "../../types";

interface SectionActionsProps {
  section: DoclistTreeItemSectionsItem;
  className?: string;
  handleOpenChange: (isOpen: boolean) => void;
  handleRename: () => void;
  onAutoRename: () => void;
  isAiRenaming?: boolean;
  categories: CategoryItem[];
  onMoveTo: () => void;
  onSplit: () => void;
  items: DoclistTreeItem[];
  onMerge: () => void;
  onDelete: () => void;
  onRotate: () => void;
  isMultiSelectMode?: boolean;
  isSelectedForMerge?: boolean;
}

interface ActionItem {
  id: string;
  icon: LucideIcon;
  messageId: string;
  defaultMessage: string;
  className?: string;
  action: () => void;
  disabled?: boolean;
}

export const SectionActions: FC<SectionActionsProps> = ({
  section,
  className = "",
  handleOpenChange,
  handleRename,
  onAutoRename,
  isAiRenaming = false,
  categories,
  onMoveTo,
  onSplit,
  items,
  onMerge,
  onDelete,
  onRotate,
  isMultiSelectMode = false,
  isSelectedForMerge = false,
}) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);

  const hasMultipleSections = items.flatMap((item) => item.sections).length > 1;

  const namingItems = [
    !isMultiSelectMode && {
      id: "editName",
      icon: Pencil,
      messageId: "docList.section.actionsDropdown.rename",
      defaultMessage: "Rename",
      action: handleRename,
    },
    !isMultiSelectMode && {
      id: "aiRename",
      icon: Sparkles,
      messageId: "docList.section.actionsDropdown.aiRename",
      defaultMessage: "AI Rename",
      action: onAutoRename,
      disabled: isAiRenaming,
    },
  ].filter(Boolean) as ActionItem[];

  const organizeItems = [
    categories.length > 0 && {
      id: "moveTo",
      icon: ArrowDownToDot,
      messageId: "docList.section.actionsDropdown.moveTo",
      defaultMessage: "Move To...",
      action: () => onMoveTo(),
    },
    !isMultiSelectMode &&
      section.pageCount > 1 && {
        id: "split",
        icon: SquareSplitVertical,
        messageId: "docList.section.actionsDropdown.split",
        defaultMessage: "Split",
        action: () => {
          onSplit();
        },
      },
    hasMultipleSections && {
      id: "merge",
      icon: Merge,
      messageId: "docList.section.actionsDropdown.merge",
      defaultMessage: "Merge",
      action: () => {
        onMerge();
      },
    },
    !isMultiSelectMode && {
      id: "rotate",
      icon: RotateCw,
      messageId: "docList.section.actionsDropdown.rotate",
      defaultMessage: "Rotate",
      action: () => {
        onRotate();
      },
    },
  ].filter(Boolean) as ActionItem[];

  const deleteItem: ActionItem | null = hasMultipleSections
    ? {
        id: "delete",
        icon: Trash2,
        messageId: "docList.category.actionsDropdown.delete",
        defaultMessage: "Delete",
        action: () => {
          onDelete();
        },
      }
    : null;

  const renderItem = (item: ActionItem) => (
    <DropdownMenuItem
      key={item.id}
      className={clsx(
        "cursor-pointer",
        item.className,
        item.disabled && "opacity-50 cursor-not-allowed"
      )}
      onClick={item.disabled ? undefined : item.action}
      destructive={item.id === "delete"}
      disabled={item.disabled}
    >
      <item.icon className="mr-2 h-4 w-4" />
      <FormattedMessage
        id={item.messageId}
        defaultMessage={item.defaultMessage}
      />
    </DropdownMenuItem>
  );

  useEffect(() => {
    handleOpenChange(open);
  }, [open]);

  useEffect(() => {
    if (open) {
      trackEvent({ eventName: "app/doc_list/document_actions.button/click" });
    }
  }, [open]);

  if (isSelectedForMerge) {
    return null;
  }

  return (
    <>
      <div onClick={(e) => e.stopPropagation()}>
        <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={clsx("h-6 w-6", className)}
              aria-label={intl.formatMessage({ id: "common.moreActions" })}
            >
              <EllipsisVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-32" align="end">
            {namingItems.map(renderItem)}
            {namingItems.length > 0 && organizeItems.length > 0 && (
              <DropdownMenuSeparator />
            )}
            {organizeItems.map(renderItem)}
            {deleteItem && (
              <>
                {(namingItems.length > 0 || organizeItems.length > 0) && (
                  <DropdownMenuSeparator />
                )}
                {renderItem(deleteItem)}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
};
